import React from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";

import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import { Box, Button, Grid, IconButton, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";

import API from "../../API";
import LoginImage from "../../Assets/login-3.jpg";
import LoginLogo from "../../Assets/LoginLogo.png";
import RoutesEnum from "../../Enums/Routes.enum";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { handleModel, showAlert, updateUser } from "../../Redux/Reducer";
import { validateFormOnSubmit } from "../../reusableFunctions/ReusableFunctions";
import { en } from "../../translate/en";
import GenerateForm from "../reusableComponents/GenerateForms";

import { OtpConfig } from "./uitilities";

const VerifyOtp = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const Theme = useTheme();
    const isMobile = useMediaQuery(Theme.breakpoints.down("sm" || "xs"));
    const user = useAppSelector((state) => state.user);

    const formikProps = useFormik({
        initialValues: {
            ...OtpConfig.map((e) => ({ [e.Name]: e.InitialValue })).reduce((a, b = {}) => ({ ...b, ...a }))
        },
        onSubmit: async (values) => {
            try {
                // Get temporary login data and construct proper OTP payload
                const tempLoginData = JSON.parse(localStorage.getItem("tempLoginData") || "{}");

                // Create the correct payload structure
                const otpData = {
                    Phone: tempLoginData.Phone_No || tempLoginData.Phone,
                    Otp: values.otp
                };

                console.log("=== OTP PAYLOAD DEBUG ===");
                console.log("tempLoginData:", tempLoginData);
                console.log("values from form:", values);
                console.log("Final otpData being sent:", otpData);

                const res: any = await API.Dealer.validateOTP(otpData);

                // Debug: Log the complete response
                console.log("=== OTP VALIDATION DEBUG ===");
                console.log("Complete API Response:", JSON.stringify(res, null, 2));
                console.log("res.data:", res?.data);
                console.log("res.data.data:", res?.data?.data);
                console.log("res.data.data.status:", res?.data?.data?.status);
                console.log("Type of status:", typeof res?.data?.data?.status);

                // Direct check of the actual API response structure
                const otpValidationStatus = res?.data?.data?.status;
                console.log("Extracted status:", otpValidationStatus);
                console.log("Is status === false?", otpValidationStatus === false);
                console.log("Is status === true?", otpValidationStatus === true);

                if (otpValidationStatus === false) {
                    console.log("Taking FALSE path - showing error");
                    // OTP is invalid - show error and don't proceed
                    // OTP is invalid - show error and don't proceed
                    dispatch(
                        showAlert({
                            open: true,
                            type: "error",
                            message: res?.data?.data?.message || "Invalid OTP",
                            closeIcon: true
                        })
                    );
                    return; // Stop execution here
                }

                if (otpValidationStatus === true) {
                    console.log("Taking TRUE path - proceeding with login");
                    // Store the JWT token in sessionStorage (cleared when tab closes)
                    sessionStorage.setItem("authToken", res?.data?.data?.token);
                    const basicUserData = res?.data?.data?.data;
                    localStorage.setItem("userId", JSON.stringify(basicUserData?.UserID));

                    // Fetch complete user data
                    try {
                        const completeUserRes: any = await API.Users.getUsersByUserID(basicUserData?.UserID);
                        if (completeUserRes?.data?.status) {
                            const completeUserData = completeUserRes?.data?.data;
                            localStorage.setItem("user", JSON.stringify(completeUserData));
                            dispatch(updateUser(completeUserData));
                        } else {
                            // Fallback to basic data if full user fetch fails
                            localStorage.setItem("user", JSON.stringify(basicUserData));
                            dispatch(updateUser(basicUserData));
                        }
                    } catch (userFetchError) {
                        // Fallback to basic data if full user fetch fails
                        localStorage.setItem("user", JSON.stringify(basicUserData));
                        dispatch(updateUser(basicUserData));
                    }

                    // Clean up temporary data
                    localStorage.removeItem("tempLoginData");
                    dispatch(
                        showAlert({
                            open: true,
                            type: "success",
                            message: res?.data?.data?.message || "OTP validated successfully",
                            closeIcon: true
                        })
                    );
                    // Close modal and navigate to dashboard
                    dispatch(handleModel({ open: false }));
                    navigate(RoutesEnum.dashboard);
                } else {
                    console.log("Taking ELSE path - unexpected status");
                    // Unexpected status value - treat as error
                    dispatch(
                        showAlert({
                            open: true,
                            type: "error",
                            message: "Unexpected response from server",
                            closeIcon: true
                        })
                    );
                }
            } catch (error: any) {
                dispatch(
                    showAlert({
                        open: true,
                        type: "error",
                        message: error?.response?.data?.message || "OTP validation failed",
                        closeIcon: true
                    })
                );
            }
        },
        validate: (values) => {
            return validateFormOnSubmit(values, [OtpConfig]);
        }
    });

    return (
        <Box component="form" onSubmit={formikProps.handleSubmit} display="flex" justifyContent="center" p={2}>
            <Paper elevation={5} sx={{ height: "auto", borderRadius: 6, width: "100%" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Box
                            component="div"
                            sx={{
                                position: "relative",
                                width: "100%",
                                borderTopLeftRadius: 15,
                                borderBottomLeftRadius: 15,
                                height: isMobile ? "auto" : "50vh"
                            }}
                        >
                            <Box
                                component="img"
                                src={LoginImage}
                                alt="signup img"
                                style={{ width: "100%", borderTopLeftRadius: 18, borderBottomLeftRadius: 18, height: "100%", objectFit: "cover" }}
                            />
                            <Box
                                style={{
                                    position: "absolute",
                                    top: "25%",
                                    left: "30%",
                                    transform: "translate(-50%, -50%)"
                                }}
                            >
                                <Box
                                    component="img"
                                    src={LoginLogo}
                                    alt="logo"
                                    style={{ width: "200px", height: "40px", border: "2px solid #fff" }}
                                />
                                <Box style={{ color: "#fff", fontWeight: "bold", marginLeft: "20px" }}>Road to Quality Cars</Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} p={2} mt={2}>
                        <Grid container lg={12} mt={1}>
                            <Grid item lg={11.5} display="flex" justifyContent="end">
                                <IconButton onClick={() => dispatch(handleModel({ open: false }))}>
                                    <HighlightOffRoundedIcon />
                                </IconButton>
                            </Grid>
                            <Grid item xs={12} pb={4} display="flex" justifyContent="start">
                                <Typography variant="h5" fontWeight={700}>
                                    {en.verifyOtp}
                                </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} mr={5} mt={-3}>
                                <Box width="100%" display="flex" justifyContent="center" gap={2} mt={2}>
                                    <GenerateForm FormData={OtpConfig} FormikProps={formikProps} />
                                </Box>
                                <Box width="100%" display="flex" justifyContent="center" mt={2} gap={2}>
                                    <Button variant="outlined" onClick={() => dispatch(handleModel({ open: true, type: "Login" }))}>
                                        {en.back}
                                    </Button>
                                    <Button variant="filled" type="submit">
                                        {en.verify}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default VerifyOtp;
